import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ConfigProvider } from "antd";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { VojomoSymbol } from "./components/VojomoSymbol";
import "./rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

/**
 * Auto-accept invite join page — replaces the old two-step accept flow.
 *
 * Flow:
 *  - Authenticated:  POST /accept/:token → redirect to session
 *  - Unauthenticated: loginWithRedirect (returnTo = this URL) → come back authenticated → same auto-accept
 */
export function RoadmapBuilderJoin() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();

  const [status, setStatus] = useState<"pending" | "accepting" | "success" | "error">("pending");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !token) return;

    if (!isAuthenticated) {
      // Redirect to Auth0 — after login, Auth0 returns here and we auto-accept
      loginWithRedirect({ appState: { returnTo: window.location.href } });
      return;
    }

    // Authenticated — auto-accept
    setStatus("accepting");
    (async () => {
      try {
        const r = await fetch(`${SERVER_URL}/api/rb/collaborators/accept/${token}`, {
          method: "POST",
          credentials: "include",
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to join roadmap.");
        }
        const data = await r.json();
        setStatus("success");
        setTimeout(() => {
          const target = data.sessionId;
          navigate(target ? `/roadmap/build_roadmap/${target}` : "/roadmap/build_roadmap");
        }, 1200);
      } catch (e: unknown) {
        setErrorMsg(e instanceof Error ? e.message : "Something went wrong.");
        setStatus("error");
      }
    })();
  }, [authLoading, isAuthenticated, token, loginWithRedirect, navigate]);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#f8fafc",
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <VojomoSymbol style={{ width: 36, height: 36 }} />
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "32px 40px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
            minWidth: 280,
          }}
        >
          {(status === "pending" || status === "accepting" || authLoading) && (
            <>
              <Loader2 style={{ width: 28, height: 28, color: "#2c1561", animation: "spin 1s linear infinite" }} />
              <p className="text-sm font-medium">Joining roadmap…</p>
              <p className="text-xs text-muted-foreground">You'll be redirected in just a moment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 style={{ width: 32, height: 32, color: "#10b981" }} />
              <p className="text-sm font-semibold">You're in!</p>
              <p className="text-xs text-muted-foreground">Taking you to the roadmap…</p>
            </>
          )}

          {status === "error" && (
            <>
              <AlertCircle style={{ width: 32, height: 32, color: "#ef4444" }} />
              <p className="text-sm font-medium">Couldn't join</p>
              <p className="text-xs text-muted-foreground">{errorMsg}</p>
              <button
                onClick={() => navigate("/roadmap/build_roadmap")}
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: "#2c1561",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Go to My Roadmaps
              </button>
            </>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
}
